import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, of } from 'rxjs';
import { catchError, switchMap, takeUntil } from 'rxjs/operators';
import { FacetClient, OrderFacets, OrderFilters } from '@interfaces';
import { SaleOrdersService } from '@services';
import { isFilterableUrgency, URGENCY_FILTER_LABELS } from '@constants';
import {
  areFiltersEqual,
  countActiveFilters,
  isDateRangeInverted,
  MAX_CLIENT_IDS,
  mergeClientIds,
  parseLocalDate,
  toLocalIsoDateString,
} from '@utils';
import { FilterMultiSelectOption } from '@components/filter-multi-select';

export type OrderFiltersDrawerResult =
  | { action: 'apply'; filters: Partial<OrderFilters> }
  | { action: 'clear' };

@Component({
  standalone: false,
  selector: 'digicem-order-filters-drawer',
  templateUrl: './order-filters-drawer.component.html',
})
export class OrderFiltersDrawerComponent implements OnInit, OnDestroy {
  protected draftDateFrom: Date | null = null;
  protected draftDateTo: Date | null = null;
  protected readonly maxDate = new Date();

  protected draftFerreteriaIds: string[] = [];
  protected draftAliasIds: string[] = [];
  protected draftUrgency: string[] = [];
  protected facets: FacetClient[] = [];
  protected isLoadingFacets = false;
  protected clientCapExceeded = false;
  protected ferreteriaOptions: FilterMultiSelectOption[] = [];
  protected aliasOptions: FilterMultiSelectOption[] = [];
  protected urgencyOptions: FilterMultiSelectOption[] = [];

  private destroy$ = new Subject<void>();
  private facetsTrigger$ = new Subject<void>();
  private hasDateRangeChangedSinceOpen = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { applied: Partial<OrderFilters> },
    private dialogRef: MatDialogRef<
      OrderFiltersDrawerComponent,
      OrderFiltersDrawerResult
    >,
    private saleOrdersService: SaleOrdersService,
  ) {}

  ngOnInit(): void {
    this.draftDateFrom = parseIsoToDate(this.data.applied.dateFrom);
    this.draftDateTo = parseIsoToDate(this.data.applied.dateTo);
    this.draftFerreteriaIds = this.data.applied.ferreteriaIds ?? [];
    this.draftAliasIds = this.data.applied.aliasIds ?? [];
    this.draftUrgency = this.data.applied.urgency ?? [];

    this.facetsTrigger$
      .pipe(
        switchMap(() => {
          this.isLoadingFacets = true;
          return this.saleOrdersService
            .getFacets(this.dateFromWire, this.dateToWire)
            .pipe(catchError(() => of(null)));
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response: OrderFacets | null) => this.onFacets(response));

    this.facetsTrigger$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get dateFromWire(): string | undefined {
    return this.draftDateFrom
      ? toLocalIsoDateString(this.draftDateFrom)
      : undefined;
  }

  private get dateToWire(): string | undefined {
    return this.draftDateTo
      ? toLocalIsoDateString(this.draftDateTo)
      : undefined;
  }

  private onFacets(response: OrderFacets | null): void {
    this.isLoadingFacets = false;
    if (!response) {
      // A failed fetch must not leave the previous date range's urgency
      // options visible/selectable for the new one (Ferretería/Alias have
      // this same latent gap, pre-existing — not extended to the new group).
      this.urgencyOptions = [];
      return;
    }

    this.facets = response.clients;
    this.ferreteriaOptions = this.facets.map(client => ({
      id: client.id,
      name: client.name,
      subDescription: client.ruc,
    }));
    this.aliasOptions = this.facets
      .filter(client => client.alias != null)
      .map(client => ({ id: client.id, name: client.alias as string }));

    if (this.hasDateRangeChangedSinceOpen && response.clients.length > 0) {
      this.draftFerreteriaIds = [];
      this.draftAliasIds = [];
    }

    // `response.urgencies` defaults to `[]` — `ApiService.get<T>` performs no
    // runtime validation, so an absent/malformed field must never throw here
    // and take the client fields above down with it.
    const urgencies = response.urgencies ?? [];
    this.urgencyOptions = urgencies
      .filter(entry => isFilterableUrgency(entry.value) && entry.count > 0)
      .map(entry => ({
        id: entry.value,
        name: isFilterableUrgency(entry.value)
          ? URGENCY_FILTER_LABELS[entry.value]
          : entry.value,
      }));
    // A selection no longer offered by the new range (group hidden, or that
    // option gone) must not silently ride along to "Ver resultados".
    this.draftUrgency = this.draftUrgency.filter(id =>
      this.urgencyOptions.some(option => option.id === id),
    );
  }

  protected get isRangeInverted(): boolean {
    return isDateRangeInverted(this.draftDateFrom, this.draftDateTo);
  }

  protected onDateFromPicked(date: Date | null): void {
    this.draftDateFrom = date;
    this.hasDateRangeChangedSinceOpen = true;
    this.facetsTrigger$.next();
  }

  protected onDateToPicked(date: Date | null): void {
    this.draftDateTo = date;
    this.hasDateRangeChangedSinceOpen = true;
    this.facetsTrigger$.next();
  }

  protected onFerreteriaChanged(ids: string[]): void {
    this.applyClientSelection('draftFerreteriaIds', ids, this.draftAliasIds);
  }

  protected onAliasChanged(ids: string[]): void {
    this.applyClientSelection('draftAliasIds', ids, this.draftFerreteriaIds);
  }

  protected onUrgencyChanged(ids: string[]): void {
    this.draftUrgency = ids;
  }

  private applyClientSelection(
    field: 'draftFerreteriaIds' | 'draftAliasIds',
    ids: string[],
    otherIds: string[],
  ): void {
    if (mergeClientIds(ids, otherIds).length > MAX_CLIENT_IDS) {
      this.clientCapExceeded = true;
      return;
    }
    this.clientCapExceeded = false;
    this[field] = ids;
  }

  protected get draftFilters(): Partial<OrderFilters> {
    return {
      dateFrom: this.dateFromWire,
      dateTo: this.dateToWire,
      ferreteriaIds: this.draftFerreteriaIds,
      aliasIds: this.draftAliasIds,
      urgency: this.draftUrgency,
    };
  }

  protected get isSubmitEnabled(): boolean {
    return (
      !this.isRangeInverted &&
      !areFiltersEqual(this.draftFilters, this.data.applied)
    );
  }

  protected get hasActiveDraftFilters(): boolean {
    return countActiveFilters(this.draftFilters) > 0;
  }

  protected onApply(): void {
    if (!this.isSubmitEnabled) return;
    this.dialogRef.close({ action: 'apply', filters: this.draftFilters });
  }

  protected onClear(): void {
    this.dialogRef.close({ action: 'clear' });
  }

  protected onClose(): void {
    this.dialogRef.close();
  }
}

function parseIsoToDate(iso?: string): Date | null {
  const parsed = iso ? parseLocalDate(iso) : null;
  return parsed ? new Date(parsed.year, parsed.month - 1, parsed.day) : null;
}
